import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms'
import {distinctUntilChanged, Subscription} from "rxjs";
import {commands, commandsCtx, defaultValueCtx, Editor, rootCtx} from "@milkdown/kit/core";
import {listener, listenerCtx} from "@milkdown/kit/plugin/listener";
import {nord} from "@milkdown/theme-nord";
import {
  commonmark,
  insertHardbreakCommand,
  toggleEmphasisCommand,
  toggleStrongCommand
} from "@milkdown/kit/preset/commonmark";
import {insert, replaceAll} from "@milkdown/utils";
import {gfm, toggleStrikethroughCommand} from '@milkdown/kit/preset/gfm';
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {take} from "rxjs/operators";
import {CoreModule} from "../../../../core.module";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {FormGroupComponent} from "../form-group/form-group.component";


@Component({
    selector: 'app-markdown-editor',
    templateUrl: './markdown-editor.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: MarkdownEditorComponent,
            multi: true
        }],
    imports: [CoreModule, TooltipModule, FormGroupComponent],
    standalone: true
})
export class MarkdownEditorComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editorContainerRef') editorContainerRef!: ElementRef;

  @Input() public label!: string
  @Input() public inline = false
  @Input() public errorMessages!: Record<string, string>
  @Input() public prefix?: string
  @Input() public name!: string
  @Input() public tooltipText?: string

  public keyboardShordcutsModal?: BsModalRef;
  public linkInputModal?: BsModalRef;
  public emailLinkModal?: BsModalRef;

  public linkInputFormControl: FormControl = new FormControl<string | null>(null, [Validators.required]);
  public emailLinkInputFormControl: FormControl = new FormControl<string | null>(null, [Validators.required, Validators.email]);


  // Base Editor model that's being used to create the active instance
  private baseEditor?: Editor;

  // Actual active Editor Instance that is being displayed
  private activeEditorInstance?: Promise<Editor>;

  private controlValueChangesSubscription?: Subscription;

  public ngOnInit() {
    this.baseEditor = Editor.make()
      // Config
      .config((ctx) => {
        // Set the Root Container where the editor is being rendered
        ctx.set(rootCtx, this.editorContainerRef.nativeElement);

        // Sets the default value / fallback value of the editor
        ctx.set(defaultValueCtx, '');

        // Editor Updates Hook
        ctx.get(listenerCtx).markdownUpdated((ctx, markdown, prevMarkdown) => {
          if (markdown !== this.control.value && markdown !== prevMarkdown) {
            this.control.setValue(
              markdown,
              {
                emitModelToViewChange: false,
                emitViewToModelChange: false
              }
            );
          }
          this.cdr.markForCheck();
        });
      })

      // Theme
      .config(nord)

      // Plugins
      .use(listener)
      .use(commonmark)
      .use(gfm)
      .use(commands);
  }

  public ngAfterViewInit() {
    this.activeEditorInstance = this.baseEditor?.create();
  }

  public ngOnDestroy() {
    this.activeEditorInstance?.then(editor => {
      editor.destroy(true);
      this.cdr.markForCheck();
    })
    this.controlValueChangesSubscription?.unsubscribe();
  }

  private updateEditorValue(): void {
    if (this.activeEditorInstance) {
      this.activeEditorInstance.then(editor => {
        editor.action(
          replaceAll(
            this.control.getRawValue() ?? ''
          )
        )
        this.cdr.markForCheck();
      });
    }
  }

  public toggleBold(): void {
    if (this.activeEditorInstance) {
      this.activeEditorInstance.then(editor => {
        editor.action((ctx) => {
          const commandManager = ctx.get(commandsCtx);
          commandManager.call(toggleStrongCommand.key);
        })
      });
    }
  }

  public toggleItalic(): void {
    if (this.activeEditorInstance) {
      this.activeEditorInstance.then(editor => {
        editor.action((ctx) => {
          const commandManager = ctx.get(commandsCtx);
          commandManager.call(toggleEmphasisCommand.key);
        })
      });
    }
  }

  public toggleStrike(): void {
    if (this.activeEditorInstance) {
      this.activeEditorInstance.then(editor => {
        editor.action((ctx) => {
          const commandManager = ctx.get(commandsCtx);
          commandManager.call(toggleStrikethroughCommand.key);
        })
      });
    }
  }

  public toggleHardLineBreak(): void {
    if (this.activeEditorInstance) {
      this.activeEditorInstance.then(editor => {
        editor.action((ctx) => {
          const commandManager = ctx.get(commandsCtx);
          commandManager.call(insertHardbreakCommand.key);
        })
      });
    }
  }

  public openLinkModal(template: TemplateRef<unknown>): void {
    this.linkInputModal = this.bsModalService.show(template);
    this.linkInputModal?.onHide?.pipe(take(1)).subscribe({
      next: () => {
        this.linkInputFormControl.reset(null);
      }
    });
  }

  public toggleLink(): void {
    if (this.linkInputFormControl.invalid) {
      this.linkInputFormControl.markAsTouched();
      return;
    }
    if (this.activeEditorInstance && this.linkInputFormControl.valid) {
      this.activeEditorInstance.then(editor => {
        editor.action(insert(` [${this.linkInputFormControl.value}](${this.linkInputFormControl.value}) \n`));
        this.linkInputModal?.hide();
        this.cdr.markForCheck();
      });
    }
  }

  public openEmailLinkModal(template: TemplateRef<unknown>): void {
    this.emailLinkModal = this.bsModalService.show(template);
    this.emailLinkModal?.onHide?.pipe(take(1)).subscribe({
      next: () => {
        this.emailLinkInputFormControl.reset(null);
      }
    });
  }

  public toggleEmailLink(): void {
    if (this.emailLinkInputFormControl.invalid) {
      this.emailLinkInputFormControl.markAsTouched();
      return;
    }
    if (this.activeEditorInstance && this.emailLinkInputFormControl.valid) {
      this.activeEditorInstance.then(editor => {
        editor.action(insert(` [${this.emailLinkInputFormControl.value}](mailto:${this.emailLinkInputFormControl.value}) \n`));
        this.emailLinkModal?.hide();
        this.cdr.markForCheck();
      });
    }
  }

  public openKeyboardShortcuts(template: TemplateRef<unknown>): void {
    this.keyboardShordcutsModal = this.bsModalService.show(template);
    this.cdr.markForCheck();
  }

  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl<string | null | undefined>

  @Input()
  public formControlName!: string;

  public get control(): FormControl<string | null | undefined> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <FormControl<string | null | undefined>>this.formControl ?? this.controlContainer.control.get(this.formControlName);
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: (v: string | null | undefined) => void): void {
    this.control.valueChanges.pipe(distinctUntilChanged()).subscribe(
      value => {
        fn(value)
      }
    )

  }

  writeValue(): void {
    this.updateEditorValue();
    this.cdr.detectChanges();
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  constructor(private readonly cdr: ChangeDetectorRef,
              private readonly injector: Injector,
              private readonly bsModalService: BsModalService
  ) {
  }


}
